import * as THREE from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import { OutputPass } from "three/examples/jsm/postprocessing/OutputPass.js";
import { QUALITY_PRESETS, autoDetectQuality, type QualityLevel, type QualitySettings } from "./QualityPresets";

type UpdateCallback = (dt: number, elapsed: number) => void;

/**
 * `window.innerWidth/innerHeight` lags or misreports on iOS Safari while its
 * address bar/toolbar is animating in or out — sizing the canvas from those
 * at the wrong moment is what causes a black bar where the toolbar used to
 * be. `visualViewport` tracks the *actual* currently-visible area and
 * updates reliably as the toolbar collapses/expands, so prefer it.
 */
function getViewportSize(): { width: number; height: number } {
  const vv = window.visualViewport;
  if (vv) return { width: Math.round(vv.width), height: Math.round(vv.height) };
  return { width: window.innerWidth, height: window.innerHeight };
}

/**
 * Owns the Three.js renderer/scene/camera and the render loop. Kept isolated
 * from multiplayer/ceremony logic per the master spec's module separation
 * (a 3D engine problem must never be able to block the ceremony).
 */
export class GameEngine {
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  readonly renderer: THREE.WebGLRenderer;
  quality: QualitySettings;

  private composer: EffectComposer | null = null;
  private bloomPass: UnrealBloomPass | null = null;
  private updateCallbacks = new Set<UpdateCallback>();
  private clock = new THREE.Clock();
  private rafHandle: number | null = null;
  private running = false;
  private disposed = false;

  constructor(private readonly canvas: HTMLCanvasElement, qualityLevel?: QualityLevel) {
    this.quality = QUALITY_PRESETS[qualityLevel ?? autoDetectQuality()];

    const initialSize = getViewportSize();
    this.camera = new THREE.PerspectiveCamera(62, initialSize.width / initialSize.height, 0.1, 500);
    this.camera.position.set(0, 4, 8);

    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: this.quality.antialias,
      powerPreference: "high-performance",
    });
    this.applyQuality(this.quality);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.setSize(initialSize.width, initialSize.height);
    this.setupComposer();

    window.addEventListener("resize", this.handleResize);
    window.addEventListener("orientationchange", this.handleResize);
    window.visualViewport?.addEventListener("resize", this.handleResize);
    window.visualViewport?.addEventListener("scroll", this.handleResize);
    // iOS Safari's toolbar collapse/expand animation can leave innerHeight
    // and visualViewport briefly stale right after load/orientation change —
    // re-check a couple of times after things settle rather than trusting
    // the very first measurement.
    window.setTimeout(this.handleResize, 300);
    window.setTimeout(this.handleResize, 1000);
  }

  private handleResize = (): void => {
    const { width, height } = getViewportSize();
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
    this.composer?.setSize(width, height);
  };

  private setupComposer(): void {
    this.composer?.dispose();
    this.composer = null;
    this.bloomPass = null;
    if (!this.quality.bloom) return;

    const { width, height } = getViewportSize();
    const composer = new EffectComposer(this.renderer);
    composer.setSize(width, height);
    composer.addPass(new RenderPass(this.scene, this.camera));
    const bloom = new UnrealBloomPass(
      new THREE.Vector2(width, height),
      0.55, // strength
      0.4, // radius
      0.78 // luminance threshold — only strongly emissive neon/energy surfaces bloom
    );
    composer.addPass(bloom);
    composer.addPass(new OutputPass());
    this.composer = composer;
    this.bloomPass = bloom;
  }

  applyQuality(settings: QualitySettings): void {
    const bloomChanged = settings.bloom !== this.quality.bloom;
    this.quality = settings;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, settings.pixelRatioCap));
    this.renderer.shadowMap.enabled = settings.shadows;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    if (bloomChanged) this.setupComposer();
  }

  onUpdate(cb: UpdateCallback): () => void {
    this.updateCallbacks.add(cb);
    return () => this.updateCallbacks.delete(cb);
  }

  start(): void {
    if (this.running || this.disposed) return;
    this.running = true;
    this.clock.start();
    const loop = () => {
      if (!this.running) return;
      const dt = Math.min(this.clock.getDelta(), 0.1);
      const elapsed = this.clock.getElapsedTime();
      for (const cb of this.updateCallbacks) cb(dt, elapsed);
      if (this.composer) this.composer.render();
      else this.renderer.render(this.scene, this.camera);
      this.rafHandle = requestAnimationFrame(loop);
    };
    this.rafHandle = requestAnimationFrame(loop);
  }

  /** Stops the render loop entirely (used before ceremony video to free the main thread, per spec 21). */
  stop(): void {
    this.running = false;
    if (this.rafHandle !== null) {
      cancelAnimationFrame(this.rafHandle);
      this.rafHandle = null;
    }
  }

  isRunning(): boolean {
    return this.running;
  }

  /** Recursively frees GPU resources for everything currently in the scene. */
  disposeSceneContents(): void {
    this.scene.traverse((obj) => {
      const mesh = obj as THREE.Mesh;
      if (mesh.geometry) mesh.geometry.dispose();
      const material = mesh.material as THREE.Material | THREE.Material[] | undefined;
      const materials = Array.isArray(material) ? material : material ? [material] : [];
      for (const mat of materials) {
        disposeMaterialTextures(mat);
        mat.dispose();
      }
    });
    while (this.scene.children.length > 0) {
      this.scene.remove(this.scene.children[0]);
    }
  }

  dispose(): void {
    this.disposed = true;
    this.stop();
    window.removeEventListener("resize", this.handleResize);
    window.removeEventListener("orientationchange", this.handleResize);
    window.visualViewport?.removeEventListener("resize", this.handleResize);
    window.visualViewport?.removeEventListener("scroll", this.handleResize);
    this.disposeSceneContents();
    this.composer?.dispose();
    this.renderer.dispose();
  }
}

const TEXTURE_MAP_KEYS = [
  "map",
  "normalMap",
  "roughnessMap",
  "metalnessMap",
  "aoMap",
  "emissiveMap",
  "alphaMap",
  "bumpMap",
] as const;

function disposeMaterialTextures(material: THREE.Material): void {
  const mat = material as unknown as Record<string, unknown>;
  for (const key of TEXTURE_MAP_KEYS) {
    const tex = mat[key];
    if (tex instanceof THREE.Texture) tex.dispose();
  }
}
